import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Alert, AlertDescription } from '../../ui/alert';
import Breadcrumb from '../../Common/Breadcrumb';
import { Actions as AGREEMENTS } from '../../../store/actions/agreementsActions';
import { toggleAlertMessage } from '../../../store/action';
import { Loader2 } from 'lucide-react';

/**
 * Platform Admin queue for consultant self-signup applications awaiting Confirm or Withdraw.
 */
const ConsultantApplications = ({
  listConsultantApplications,
  confirmConsultantApplication,
  withdrawConsultantApplication,
  showAlert,
}) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    const result = await listConsultantApplications();
    setApplications(result.applications || []);
    setLoading(false);
  }, [listConsultantApplications]);

  useEffect(() => {
    reload();
  }, [reload]);

  const onConfirm = async (id) => {
    setBusyId(id);
    const result = await confirmConsultantApplication(id);
    setBusyId(null);
    if (result.ok) {
      showAlert(true, 'Consultancy confirmed. Provisioning has started.', 'success', 4000);
      reload();
    } else {
      showAlert(true, result.error || 'Could not confirm', 'error', 6000);
    }
  };

  const onWithdraw = async (id) => {
    setBusyId(id);
    const result = await withdrawConsultantApplication(id, 'Withdrawn by platform admin');
    setBusyId(null);
    if (result.ok) {
      showAlert(true, 'Application withdrawn. Acceptance evidence is retained.', 'success', 4000);
      reload();
    } else {
      showAlert(true, result.error || 'Could not withdraw', 'error', 6000);
    }
  };

  return (
    <div className='p-6 space-y-4'>
      <Breadcrumb
        pages={[
          { name: 'Admin', path: '/admin' },
          { name: 'Agreements', path: '/admin/agreements' },
          { name: 'Consultant applications', path: '/admin/consultant-applications' },
        ]}
      />
      <Card className='bg-card text-card-foreground'>
        <CardHeader>
          <CardTitle>Pending consultant applications</CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          <Alert>
            <AlertDescription className='text-xs'>
              Self-signup applicants accept the Consultant Pricing Schedule first. Confirm grants consultant admin and
              client-creation rights. Withdraw voids the agreement and keeps the acceptance evidence.
            </AlertDescription>
          </Alert>
          {loading ? (
            <div className='flex items-center gap-2 text-muted-foreground'>
              <Loader2 className='h-4 w-4 animate-spin' />
              Loading…
            </div>
          ) : applications.length === 0 ? (
            <p className='text-sm text-muted-foreground'>No applications awaiting confirmation.</p>
          ) : (
            <div className='space-y-3'>
              {applications.map((app) => (
                <div
                  key={app.id}
                  className='border border-border rounded-md p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3'>
                  <div className='text-sm space-y-1'>
                    <p className='font-semibold'>{app.customerOrganisationName}</p>
                    <p className='text-muted-foreground'>
                      {app.customerFirstName} {app.customerLastName} · {app.customerEmail}
                    </p>
                    <p className='text-xs text-muted-foreground'>
                      Accepted {app.acceptedAt ? String(app.acceptedAt).slice(0, 10) : '—'} · {app.billingCurrency}
                    </p>
                  </div>
                  <div className='flex gap-2'>
                    <Button
                      size='sm'
                      loading={busyId === app.id}
                      onClick={() => onConfirm(app.id)}
                      disabled={!!busyId}>
                      Confirm
                    </Button>
                    <Button
                      size='sm'
                      variant='outline'
                      loading={busyId === app.id}
                      onClick={() => onWithdraw(app.id)}
                      disabled={!!busyId}>
                      Withdraw
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  listConsultantApplications: () => dispatch(AGREEMENTS.listConsultantApplications()),
  confirmConsultantApplication: (id) => dispatch(AGREEMENTS.confirmConsultantApplication(id)),
  withdrawConsultantApplication: (id, reason) => dispatch(AGREEMENTS.withdrawConsultantApplication(id, reason)),
  showAlert: (status, message, severity, hideDuration) =>
    dispatch(toggleAlertMessage(status, message, severity, hideDuration)),
});

export default connect(null, mapDispatchToProps)(ConsultantApplications);
